import { SETTINGS_SIDEBAR_ITEMS_FLAT } from "@/components/settings/settings.constants";
import {
  addDashboardFixtureCaptureEventListener,
  getDashboardFixtureCapturePendingCount,
  isDashboardFixtureCaptureEnabled,
} from "@/utils/connect-query/fixtures/dashboard-fixture-capture";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { Routes } from "@/utils/routes";
import { IdStorageImpl } from "@/utils/storage/id-storage";
import type { NextRouter } from "next/router";

const DASHBOARD_FIXTURE_CAPTURE_CRAWL_STATE_VERSION = 1;
const CAPTURE_CRAWL_IDLE_MS = 750;
const CAPTURE_CRAWL_MAX_WAIT_MS = 8_000;

interface DashboardFixtureCaptureCrawlState {
  completed: boolean;
  completedRoutes: string[];
  originRoute: string;
  routes: string[];
  startedAt: string;
  version: typeof DASHBOARD_FIXTURE_CAPTURE_CRAWL_STATE_VERSION;
}

export interface DashboardFixtureCaptureCrawlRouteInput {
  currentRoute: string;
  environmentId?: string;
  projectId?: string;
}

interface AdvanceDashboardFixtureCaptureCrawlInput extends DashboardFixtureCaptureCrawlRouteInput {
  router: NextRouter;
  shouldContinue?: () => boolean;
}

export function clearDashboardFixtureCaptureCrawlState(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(LocalStorageKey.DASHBOARD_FIXTURE_CAPTURE_CRAWL_STATE);
}

export function normalizeDashboardFixtureCaptureRoute(route: string): string | null {
  const trimmedRoute = route.trim();
  if (!trimmedRoute) {
    return null;
  }

  if (trimmedRoute.startsWith("/")) {
    try {
      const url = new URL(trimmedRoute, "https://chalk.ai");
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  if (!/^https?:\/\//i.test(trimmedRoute)) {
    return null;
  }

  try {
    const url = new URL(trimmedRoute);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function getDashboardFixtureCaptureCrawlRoutes({
  currentRoute,
  environmentId,
  projectId,
}: DashboardFixtureCaptureCrawlRouteInput): string[] {
  const storedProject = IdStorageImpl.getProject();
  const resolvedProjectId = projectId ?? storedProject?.projectId;
  const resolvedEnvironmentId = environmentId ?? storedProject?.environmentId;

  const routeCandidates = [
    currentRoute,
    Routes.projects,
    Routes.team.settings(),
    Routes.team.settings("sso"),
    resolvedProjectId != null ? Routes.project.index(resolvedProjectId) : null,
    ...(resolvedProjectId != null && resolvedEnvironmentId != null
      ? [
          Routes.project.environment.dashboard(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.features(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.deployments(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.branches(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.namedQueries(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.metaQueryRuns(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.offlineQueries(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.datasets(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.tiles(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.logs.audit(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.traces(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.dataSources(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.jobs(resolvedProjectId, resolvedEnvironmentId),
          Routes.project.environment.incidents(resolvedProjectId, resolvedEnvironmentId),
          ...SETTINGS_SIDEBAR_ITEMS_FLAT.map((pane) =>
            Routes.project.environment.settings(resolvedProjectId, resolvedEnvironmentId, pane.id),
          ),
        ]
      : []),
  ];

  return uniqueRoutes(routeCandidates);
}

export async function advanceDashboardFixtureCaptureCrawl({
  currentRoute,
  environmentId,
  projectId,
  router,
  shouldContinue = () => true,
}: AdvanceDashboardFixtureCaptureCrawlInput): Promise<void> {
  if (typeof window === "undefined" || !isDashboardFixtureCaptureEnabled()) {
    return;
  }

  const normalizedCurrentRoute = normalizeDashboardFixtureCaptureRoute(currentRoute);
  if (normalizedCurrentRoute == null) {
    return;
  }

  const state = getOrCreateDashboardFixtureCaptureCrawlState({
    currentRoute: normalizedCurrentRoute,
    environmentId,
    projectId,
  });
  if (state.completed) {
    return;
  }

  await waitForDashboardFixtureCaptureIdle();
  if (!shouldContinue() || !isDashboardFixtureCaptureEnabled()) {
    return;
  }

  const nextState = readDashboardFixtureCaptureCrawlState() ?? state;
  if (nextState.completed) {
    return;
  }

  nextState.completedRoutes = uniqueRoutes([...nextState.completedRoutes, normalizedCurrentRoute]);

  const nextRoute = nextState.routes.find((route) => !nextState.completedRoutes.includes(route));
  if (nextRoute != null) {
    writeDashboardFixtureCaptureCrawlState(nextState);
    if (nextRoute !== normalizedCurrentRoute) {
      await router.push(nextRoute);
    }
    return;
  }

  nextState.completed = true;
  writeDashboardFixtureCaptureCrawlState(nextState);
  if (normalizedCurrentRoute !== nextState.originRoute) {
    await router.push(nextState.originRoute);
  }
}

function getOrCreateDashboardFixtureCaptureCrawlState({
  currentRoute,
  environmentId,
  projectId,
}: DashboardFixtureCaptureCrawlRouteInput): DashboardFixtureCaptureCrawlState {
  const existingState = readDashboardFixtureCaptureCrawlState();
  const routes = getDashboardFixtureCaptureCrawlRoutes({
    currentRoute,
    environmentId,
    projectId,
  });

  if (existingState != null) {
    existingState.routes = uniqueRoutes([...existingState.routes, ...routes]);
    writeDashboardFixtureCaptureCrawlState(existingState);
    return existingState;
  }

  const state: DashboardFixtureCaptureCrawlState = {
    completed: false,
    completedRoutes: [],
    originRoute: currentRoute,
    routes,
    startedAt: new Date().toISOString(),
    version: DASHBOARD_FIXTURE_CAPTURE_CRAWL_STATE_VERSION,
  };
  writeDashboardFixtureCaptureCrawlState(state);
  return state;
}

async function waitForDashboardFixtureCaptureIdle(): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }

  await new Promise<void>((resolve) => {
    let idleTimer: number | null = null;
    let maxTimer: number | null = null;
    let unsubscribe: (() => void) | null = null;

    const clearTimers = () => {
      if (idleTimer != null) {
        window.clearTimeout(idleTimer);
        idleTimer = null;
      }
      if (maxTimer != null) {
        window.clearTimeout(maxTimer);
        maxTimer = null;
      }
    };

    const finish = () => {
      clearTimers();
      unsubscribe?.();
      resolve();
    };

    const scheduleIdleTimerIfReady = () => {
      if (idleTimer != null) {
        window.clearTimeout(idleTimer);
        idleTimer = null;
      }

      if (getDashboardFixtureCapturePendingCount() === 0) {
        idleTimer = window.setTimeout(finish, CAPTURE_CRAWL_IDLE_MS);
      }
    };

    unsubscribe = addDashboardFixtureCaptureEventListener(scheduleIdleTimerIfReady);
    maxTimer = window.setTimeout(finish, CAPTURE_CRAWL_MAX_WAIT_MS);
    scheduleIdleTimerIfReady();
  });
}

function readDashboardFixtureCaptureCrawlState(): DashboardFixtureCaptureCrawlState | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(
      LocalStorageKey.DASHBOARD_FIXTURE_CAPTURE_CRAWL_STATE,
    );
    if (raw == null) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<DashboardFixtureCaptureCrawlState>;
    if (
      parsed.version !== DASHBOARD_FIXTURE_CAPTURE_CRAWL_STATE_VERSION ||
      typeof parsed.originRoute !== "string" ||
      !Array.isArray(parsed.routes) ||
      !Array.isArray(parsed.completedRoutes)
    ) {
      return null;
    }

    return {
      completed: parsed.completed === true,
      completedRoutes: uniqueRoutes(parsed.completedRoutes),
      originRoute: parsed.originRoute,
      routes: uniqueRoutes(parsed.routes),
      startedAt: typeof parsed.startedAt === "string" ? parsed.startedAt : new Date().toISOString(),
      version: DASHBOARD_FIXTURE_CAPTURE_CRAWL_STATE_VERSION,
    };
  } catch {
    return null;
  }
}

function writeDashboardFixtureCaptureCrawlState(state: DashboardFixtureCaptureCrawlState): void {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    LocalStorageKey.DASHBOARD_FIXTURE_CAPTURE_CRAWL_STATE,
    JSON.stringify(state),
  );
}

function uniqueRoutes(routes: Array<string | null | undefined>): string[] {
  const normalizedRoutes = new Set<string>();
  for (const route of routes) {
    if (route == null) {
      continue;
    }

    const normalizedRoute = normalizeDashboardFixtureCaptureRoute(route);
    if (normalizedRoute == null) {
      continue;
    }

    normalizedRoutes.add(normalizedRoute);
  }
  return Array.from(normalizedRoutes);
}
